import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const PROMO_TIMEZONE = "America/Los_Angeles";

// Returns the UTC Date for midnight on `dateStr` in the given IANA timezone.
// Uses Intl to resolve the offset correctly — handles DST automatically.
function midnightIn(tz: string, dateStr: string): Date {
  const probe = new Date(`${dateStr}T12:00:00Z`); // noon UTC — safely same day in any tz
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(probe);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  const offsetMs =
    probe.getTime() -
    new Date(
      `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}:${get("second")}Z`,
    ).getTime();
  return new Date(new Date(`${dateStr}T00:00:00Z`).getTime() + offsetMs);
}

// Promo extended again → expires at midnight Sep 21 2026, California time
// (originally a 60-day run from Apr 21 that ended Jun 21 2026, then Aug 21).
const PROMO_END = midnightIn(PROMO_TIMEZONE, "2026-09-21");

interface PromoStripProps {
  isYearly: boolean;
  isCompact: boolean;
  onSwitchToYearly: () => void;
}

export default function PromoStrip({
  isYearly,
  isCompact,
  onSwitchToYearly,
}: PromoStripProps) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (Date.now() >= PROMO_END.getTime()) {
      return;
    }
    const id = setInterval(() => {
      const ts = Date.now();
      setNow(ts);
      if (ts >= PROMO_END.getTime()) {
        clearInterval(id);
      }
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  const ms = PROMO_END.getTime() - now;
  if (ms <= 0) {
    return null;
  }
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor((ms / 3_600_000) % 24);
  const m = Math.floor((ms / 60_000) % 60);
  const endsInText = t("subscription.promoEndsInDays", { d });

  if (isCompact) {
    return (
      <div className="flex items-center justify-between gap-2.5 border-y border-green-500/25 bg-gradient-to-r from-green-500/10 via-green-400/10 to-green-500/10 px-4 py-2.5">
        <p className="text-center text-[12px] text-foreground">
          {isYearly ? (
            <>
              <strong className="text-green-400">
                {t("subscription.promoOffYearly")}
              </strong>{" "}
              · {endsInText}
            </>
          ) : (
            <>
              <strong className="text-green-400">
                {t("subscription.promoSaveWithYearly")}
              </strong>{" "}
              · {endsInText}
            </>
          )}
        </p>
        {!isYearly && (
          <button
            type="button"
            onClick={onSwitchToYearly}
            className="shrink-0 rounded-md bg-green-500 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-green-400"
          >
            {t("subscription.promoSwitchArrow")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto mt-9 max-w-[1240px] px-5">
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3.5 rounded-xl border border-green-500/25 bg-gradient-to-r from-green-500/10 via-green-400/10 to-green-500/10 px-5 py-3">
        <span className="flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-[0.16em] text-green-400">
          <span className="size-1.5 rounded-full bg-green-400 shadow-[0_0_6px_hsl(142_71%_60%)]" />
          {t("subscription.limitedTime")}
        </span>

        <p className="text-[13px] text-foreground">
          {isYearly ? (
            <>
              <strong className="text-green-400">
                {t("subscription.promoOff")}
              </strong>{" "}
              {t("subscription.promoOffApplied")}
            </>
          ) : (
            <>
              <strong className="text-green-400">
                {t("subscription.promoSave")}
              </strong>{" "}
              {t("subscription.promoSwitchSuffix")}
            </>
          )}
        </p>

        <div className="flex items-center gap-1.5">
          {[
            { val: d, label: t("subscription.countdownD") },
            { val: h, label: t("subscription.countdownH") },
            { val: m, label: t("subscription.countdownM") },
          ].map(({ val, label }) => (
            <span
              key={label}
              className="flex min-w-[32px] flex-col items-center rounded-md border border-border bg-background/60 px-1.5 py-1 text-center tabular-nums"
            >
              <b className="block text-[12px] font-bold leading-none">
                {String(val).padStart(2, "0")}
              </b>
              <span className="text-[8px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
              </span>
            </span>
          ))}
          {!isYearly && (
            <button
              type="button"
              onClick={onSwitchToYearly}
              className="ml-1 rounded-md bg-green-500 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-green-400"
            >
              {t("subscription.promoSwitchToYearly")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
